import React, { useState, useEffect } from 'react';
import { Card, Button, Badge, Spinner, Alert, Row, Col, Nav, Modal } from 'react-bootstrap';
import {
    FaEye,
    FaDownload,
    FaUserTie,
    FaFileContract,
    FaExclamationTriangle,
    FaCheckCircle,
    FaClock,
    FaFilter,
    FaTimesCircle,
    FaFilePdf,
    FaSignature,
    FaTimes
} from 'react-icons/fa';
import HeaderTopSection from "../../components/partials/HeaderTop";
import ContractModal from '../../components/contract/ContractModal';
import PDFViewerModal from '../../components/PDFViewerModal';
import ContractService from '../../services/ContractService';
import '../../assets/css/ContractsPage.css';
import Banner from "../../components/partials/Banner";
import AdminSignatureModal from '../../components/contract/client/AdminSignatureModal';

const ContractsPage = () => {
    const [contracts, setContracts] = useState([]);
    const [filteredContracts, setFilteredContracts] = useState([]);
    const [activeFilter, setActiveFilter] = useState('DRAFT');
    const [selectedContract, setSelectedContract] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [downloadingId, setDownloadingId] = useState(null);
    const [generatingId, setGeneratingId] = useState(null);
    const [showPdfModal, setShowPdfModal] = useState(false);
    const [pdfData, setPdfData] = useState(null);
    const [showAdminSignatureModal, setShowAdminSignatureModal] = useState(false);
    const [contractToAdminSign, setContractToAdminSign] = useState(null);
    const [showPdfViewer, setShowPdfViewer] = useState(false);
    const [selectedPdfUrl, setSelectedPdfUrl] = useState(null);
    const [selectedPdfContractId, setSelectedPdfContractId] = useState(null);
    const [selectedPdfTitle, setSelectedPdfTitle] = useState('Document PDF');
    const [pdfLoading, setPdfLoading] = useState(false);

    useEffect(() => {
        fetchContracts();
    }, []);

    useEffect(() => {
        filterContracts();
    }, [contracts, activeFilter]);

    const fetchContracts = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const contractsData = await ContractService.getAllContracts();
            setContracts(contractsData);
        } catch (err) {
            console.error('Failed to fetch contracts:', err);
            setError('Échec du chargement des contrats. Veuillez réessayer.');
        } finally {
            setIsLoading(false);
        }
    };

    const filterContracts = () => {
        let filtered = [];

        switch (activeFilter) {
            case 'DRAFT':
                filtered = contracts.filter(contract =>
                    contract.contractStatus === 'DRAFT'
                );
                break;

            case 'PENDING_SIGNATURE_CLIENT_FALSE':
                filtered = contracts.filter(contract =>
                    contract.contractStatus === 'PENDING_SIGNATURE_CLIENT' &&
                    !contract.clientSigned
                );
                break;

            case 'PENDING_SIGNATURE_CLIENT_TRUE':
                filtered = contracts.filter(contract =>
                    (contract.contractStatus === 'PENDING_SIGNATURE_CLIENT' && contract.clientSigned) ||
                    contract.contractStatus === 'PENDING_SIGNATURE_CLIENT_DHS'
                );
                break;

            case 'ACTIVE':
                filtered = contracts.filter(contract =>
                    contract.contractStatus === 'ACTIVE'
                );
                break;

            case 'COMPLETED':
                filtered = contracts.filter(contract =>
                    contract.contractStatus === 'TERMINATED' ||
                    contract.contractStatus === 'EXPIRED' ||
                    contract.contractStatus === 'CANCELLED'
                );
                break;

            default:
                filtered = contracts;
        }

        setFilteredContracts(filtered);
    };

    const handleFilterChange = (filter) => {
        setActiveFilter(filter);
    };

    const handleViewContract = (contract) => {
        setSelectedContract(contract);
        setIsModalOpen(true);
    };

    /**
     * Fonction générique pour ouvrir la modale PDF avec n'importe quelle URL
     * Télécharge le PDF via fetch pour contourner les problèmes CORS
     */
    const handleViewPdf = async (url, contractRef, title = 'Document PDF') => {
        if (!url) {
            alert('Aucun PDF disponible pour ce document');
            return;
        }

        setPdfLoading(true);
        setSelectedPdfTitle(title);
        setSelectedPdfContractId(contractRef);

        try {
            // Télécharger le PDF via fetch pour contourner les problèmes CORS
            const response = await fetch(url, {
                mode: 'cors',
                headers: {
                    'Accept': 'application/pdf',
                }
            });

            if (!response.ok) {
                throw new Error(`Erreur HTTP: ${response.status}`);
            }

            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);
            setSelectedPdfUrl(blobUrl);
            setShowPdfViewer(true);
        } catch (error) {
            console.error('Erreur lors du chargement du PDF:', error);
            // Fallback: essayer d'ouvrir directement l'URL
            setSelectedPdfUrl(url);
            setShowPdfViewer(true);
        } finally {
            setPdfLoading(false);
        }
    };

    // Contrat généré (en attente de signature)
    const handleViewGeneratedContract = (contract) => {
        const url = contract.generatedContractPresigned || contract.generatedContract;
        handleViewPdf(url, contract.contractRef, `Contrat Généré - ${contract.contractRef}`);
    };

    // Document signé par le client
    const handleViewClientSignedDoc = (contract) => {
        const url = contract.contractDocClientSignedPresigned || contract.contractDocClientSigned;
        handleViewPdf(url, contract.contractRef, `Contrat Signé par le Client - ${contract.contractRef}`);
    };

    // Contrat actif (signé par DHS)
    const handleViewActiveContract = (contract) => {
        const url = contract.contractClientActivePresigned || contract.contractClientActive;
        handleViewPdf(url, contract.contractRef, `Contrat Actif - ${contract.contractRef}`);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedContract(null);
    };

    const closePdfViewer = () => {
        setShowPdfViewer(false);
        // Libérer l'URL blob si c'en est une
        if (selectedPdfUrl && selectedPdfUrl.startsWith('blob:')) {
            URL.revokeObjectURL(selectedPdfUrl);
        }
        setSelectedPdfUrl(null);
        setSelectedPdfContractId(null);
        setSelectedPdfTitle('Document PDF');
        setPdfLoading(false);
    };

    const generateFileName = (contract) => {
        const contractId = contract.id;
        const clientName = getFullName(contract.client?.user).replace(/\s+/g, '-');
        const endDate = formatDateForFileName(contract.endDate);
        const randomSuffix = Math.floor(1000 + Math.random() * 9000);

        return `CONTRACT-${contractId}-${clientName}-${endDate}-${randomSuffix}.pdf`;
    };

    const formatDateForFileName = (timestamp) => {
        if (!timestamp) return 'NO-DATE';
        const date = new Date(timestamp);
        return `${date.getDate().toString().padStart(2, '0')}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getFullYear()}`;
    };

    const getCurrentUser = () => {
        return localStorage.getItem('username') || 'admin';
    };

    const handleGenerateContract = async (contract) => {
        setGeneratingId(contract.id);
        try {
            // 1. Générer le PDF via l'API
            const pdfBytes = await ContractService.generateContractPdf(contract.id);

            // 2. Convertir les bytes en Blob
            const pdfBlob = new Blob([pdfBytes], { type: 'application/pdf' });

            // 3. Créer un blob URL pour affichage
            const blobUrl = URL.createObjectURL(pdfBlob);
            setPdfData(blobUrl);
            setShowPdfModal(true);

            // 4. Rafraîchir la liste des contrats
            fetchContracts();

        } catch (error) {
            console.error('Erreur lors de la génération du contrat:', error);
            alert(`Erreur lors de la génération: ${error.message}`);
        } finally {
            setGeneratingId(null);
        }
    };

    const handleAdminSignContract = (contract) => {
        setContractToAdminSign(contract);
        setShowAdminSignatureModal(true);
    };

    const closePdfModal = () => {
        setShowPdfModal(false);
        if (pdfData && pdfData.startsWith('blob:')) {
            URL.revokeObjectURL(pdfData);
            setPdfData(null);
        }
    };

    const handleDownloadPdfFromModal = () => {
        if (pdfData) {
            const link = document.createElement('a');
            link.href = pdfData;
            link.download = `contrat_${selectedContract?.contractRef || 'document'}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return 'Non spécifié';
        return new Date(timestamp).toLocaleDateString('fr-FR', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'DRAFT':
                return <Badge bg="secondary" className="fs-6 py-2 px-3 rounded-pill">
                    <FaClock className="me-1" /> En attente génération
                </Badge>;
            case 'PENDING_SIGNATURE_CLIENT':
                return <Badge bg="warning" className="fs-6 py-2 px-3 rounded-pill">
                    <FaSignature className="me-1" /> Attente client
                </Badge>;
            case 'PENDING_SIGNATURE_CLIENT_DHS':
                return <Badge bg="success" className="fs-6 py-2 px-3 rounded-pill">
                    <FaSignature className="me-1" /> Client signé
                </Badge>;
            case 'ACTIVE':
                return <Badge bg="primary" className="fs-6 py-2 px-3 rounded-pill">
                    <FaCheckCircle className="me-1" /> Actif
                </Badge>;
            case 'EXPIRED':
                return <Badge bg="danger" className="fs-6 py-2 px-3 rounded-pill">
                    <FaTimesCircle className="me-1" /> Expiré
                </Badge>;
            case 'TERMINATED':
                return <Badge bg="success" className="fs-6 py-2 px-3 rounded-pill">
                    <FaCheckCircle className="me-1" /> Terminé
                </Badge>;
            case 'CANCELLED':
                return <Badge bg="dark" className="fs-6 py-2 px-3 rounded-pill">
                    <FaTimesCircle className="me-1" /> Annulé
                </Badge>;
            default:
                return <Badge bg="secondary" className="fs-6 py-2 px-3 rounded-pill">Inconnu</Badge>;
        }
    };

    const getAvatarUrl = (user) => {
        return user?.profilePictureUrl || 'https://yrbhgwbzukfejvcphnwa.supabase.co/storage/v1/object/public/driven-home-services/media-service/dhs.png';
    };

    const getFullName = (user) => {
        if (!user) return 'Information non disponible';
        return `${user.firstName} ${user.lastName}`.trim();
    };

    const getEmail = (user) => {
        return user?.email || 'Email non disponible';
    };

    const getPhone = (user) => {
        return user?.phoneNumber || 'Téléphone non disponible';
    };

    const getLocation = (user) => {
        if (!user) return 'Localisation non disponible';
        return `${user.city || ''}, ${user.country || ''}`.trim().replace(/^,\s*|\s*,$/g, '');
    };

    const getServiceLabel = (service) => {
        if (!service) return 'Service non spécifié';
        const serviceLabels = {
            'garde_enfant': 'Garde d\'enfant',
            'menage': 'Ménage',
            'cuisine': 'Cuisine',
            'jardinage': 'Jardinage'
        };
        return serviceLabels[service] || service.replace('_', ' ');
    };

    const getFilterStats = () => {
        return {
            DRAFT: contracts.filter(c => c.contractStatus === 'DRAFT').length,
            PENDING_SIGNATURE_CLIENT_FALSE: contracts.filter(c =>
                c.contractStatus === 'PENDING_SIGNATURE_CLIENT' && !c.clientSigned
            ).length,
            PENDING_SIGNATURE_CLIENT_TRUE: contracts.filter(c =>
                (c.contractStatus === 'PENDING_SIGNATURE_CLIENT' && c.clientSigned) ||
                c.contractStatus === 'PENDING_SIGNATURE_CLIENT_DHS'
            ).length,
            ACTIVE: contracts.filter(c => c.contractStatus === 'ACTIVE').length,
            COMPLETED: contracts.filter(c =>
                ['TERMINATED', 'EXPIRED', 'CANCELLED'].includes(c.contractStatus)
            ).length
        };
    };

    const filterStats = getFilterStats();

    /**
     * Fonction qui retourne le bouton d'action en fonction du filtre actif
     * et du contrat sélectionné
     */
    const getActionButton = (contract) => {
        switch (activeFilter) {
            case 'DRAFT':
                return (
                    <Button
                        variant="primary"
                        className="w-100"
                        onClick={() => handleGenerateContract(contract)}
                        disabled={generatingId === contract.id}
                        size="lg"
                    >
                        {generatingId === contract.id ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Génération...
                            </>
                        ) : (
                            <>
                                <FaFilePdf className="me-2" /> Générer le contrat
                            </>
                        )}
                    </Button>
                );

            case 'PENDING_SIGNATURE_CLIENT_FALSE':
                // Vérifier si le contrat généré existe
                const generatedUrl = contract.generatedContractPresigned || contract.generatedContract;
                if (generatedUrl) {
                    return (
                        <Button
                            variant="outline-primary"
                            className="w-100"
                            onClick={() => handleViewGeneratedContract(contract)}
                            size="lg"
                            disabled={pdfLoading}
                        >
                            {pdfLoading ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Chargement...
                                </>
                            ) : (
                                <>
                                    <FaEye className="me-2" /> Voir le contrat généré
                                </>
                            )}
                        </Button>
                    );
                }
                return (
                    <Button
                        variant="outline-secondary"
                        className="w-100"
                        disabled
                        size="lg"
                    >
                        <FaClock className="me-2" /> En attente de génération
                    </Button>
                );

            case 'PENDING_SIGNATURE_CLIENT_TRUE':
                const clientSignedUrl = contract.contractDocClientSignedPresigned || contract.contractDocClientSigned;
                return (
                    <div className="d-flex flex-column gap-2 w-100">
                        {clientSignedUrl && (
                            <Button
                                variant="outline-success"
                                className="w-100"
                                onClick={() => handleViewClientSignedDoc(contract)}
                                size="lg"
                                disabled={pdfLoading}
                            >
                                {pdfLoading ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        Chargement...
                                    </>
                                ) : (
                                    <>
                                        <FaEye className="me-2" /> Voir document signé
                                    </>
                                )}
                            </Button>
                        )}
                        <Button
                            variant="success"
                            className="w-100"
                            onClick={() => handleAdminSignContract(contract)}
                            size="lg"
                        >
                            <FaSignature className="me-2" /> Signer le contrat
                        </Button>
                    </div>
                );

            case 'ACTIVE':
                const activeUrl = contract.contractClientActivePresigned || contract.contractClientActive;
                if (activeUrl) {
                    return (
                        <Button
                            variant="success"
                            className="w-100"
                            onClick={() => handleViewActiveContract(contract)}
                            size="lg"
                            disabled={pdfLoading}
                        >
                            {pdfLoading ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Chargement...
                                </>
                            ) : (
                                <>
                                    <FaEye className="me-2" /> Afficher le PDF
                                </>
                            )}
                        </Button>
                    );
                }
                return (
                    <Button
                        variant="outline-secondary"
                        className="w-100"
                        disabled
                        size="lg"
                    >
                        <FaClock className="me-2" /> En attente d'activation
                    </Button>
                );

            default:
                return (
                    <Button
                        variant="success"
                        className="w-100"
                        onClick={() => handleViewActiveContract(contract)}
                        size="lg"
                        disabled={pdfLoading}
                    >
                        {pdfLoading ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Chargement...
                            </>
                        ) : (
                            <>
                                <FaEye className="me-2" /> Afficher le PDF
                            </>
                        )}
                    </Button>
                );
        }
    };

    return (
        <>
            <HeaderTopSection />
            <Banner
                title="Gestion des Contrats"
                subtitle="Administrez et suivez tous les contrats de service"
            />

            <div className="container-fluid py-4" style={{ backgroundColor: '#f8fafc', minHeight: '100vh' }}>
                <div className="container">
                    <div className="row">
                        {/* Sidebar de filtrage */}
                        <div className="col-lg-3 mb-7">
                            <Card className="shadow-sm border-0 sticky-top" style={{ top: '10px', borderRadius: '16px', overflow: 'hidden' }}>
                                <Card.Header className="bg-gradient text-white border-0 py-4" style={{
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                }}>
                                    <div className="d-flex align-items-center justify-content-center">
                                        <FaFilter className="me-2" size={20} />
                                        <h5 className="mb-0 fw-bold">Filtres des Contrats</h5>
                                    </div>
                                </Card.Header>
                                <Card.Body className="p-0">
                                    <Nav variant="pills" className="flex-column">
                                        {/* Contrats à Générer */}
                                        <Nav.Item>
                                            <Nav.Link
                                                className={`filter-nav-link d-flex justify-content-between align-items-center py-3 px-4 ${activeFilter === 'DRAFT' ? 'active-pending' : ''}`}
                                                onClick={() => handleFilterChange('DRAFT')}
                                                style={{
                                                    cursor: 'pointer',
                                                    borderRadius: 0,
                                                    borderLeft: activeFilter === 'DRAFT' ? '4px solid #6c757d' : '4px solid transparent',
                                                    transition: 'all 0.3s ease',
                                                    backgroundColor: activeFilter === 'DRAFT' ? '#e9ecef' : 'transparent'
                                                }}
                                            >
                                                <span className="d-flex align-items-center">
                                                    <FaClock className="me-2" style={{ color: activeFilter === 'DRAFT' ? '#6c757d' : '#6c757d' }} />
                                                    <span className={`fw-${activeFilter === 'DRAFT' ? 'bold' : 'medium'}`}>
                                                        À Générer
                                                    </span>
                                                </span>
                                                <Badge
                                                    bg={activeFilter === 'DRAFT' ? 'secondary' : 'light'}
                                                    text={activeFilter === 'DRAFT' ? 'white' : 'secondary'}
                                                    pill
                                                    className="px-3 py-2"
                                                >
                                                    {filterStats.DRAFT}
                                                </Badge>
                                            </Nav.Link>
                                        </Nav.Item>

                                        {/* En Attente Client */}
                                        <Nav.Item>
                                            <Nav.Link
                                                className={`filter-nav-link d-flex justify-content-between align-items-center py-3 px-4 ${activeFilter === 'PENDING_SIGNATURE_CLIENT_FALSE' ? 'active-pending' : ''}`}
                                                onClick={() => handleFilterChange('PENDING_SIGNATURE_CLIENT_FALSE')}
                                                style={{
                                                    cursor: 'pointer',
                                                    borderRadius: 0,
                                                    borderLeft: activeFilter === 'PENDING_SIGNATURE_CLIENT_FALSE' ? '4px solid #ffc107' : '4px solid transparent',
                                                    transition: 'all 0.3s ease',
                                                    backgroundColor: activeFilter === 'PENDING_SIGNATURE_CLIENT_FALSE' ? '#fff3cd' : 'transparent'
                                                }}
                                            >
                                                <span className="d-flex align-items-center">
                                                    <FaSignature className="me-2" style={{ color: activeFilter === 'PENDING_SIGNATURE_CLIENT_FALSE' ? '#ffc107' : '#6c757d' }} />
                                                    <span className={`fw-${activeFilter === 'PENDING_SIGNATURE_CLIENT_FALSE' ? 'bold' : 'medium'}`}>
                                                        En Attente Client
                                                    </span>
                                                </span>
                                                <Badge
                                                    bg={activeFilter === 'PENDING_SIGNATURE_CLIENT_FALSE' ? 'warning' : 'light'}
                                                    text={activeFilter === 'PENDING_SIGNATURE_CLIENT_FALSE' ? 'dark' : 'secondary'}
                                                    pill
                                                    className="px-3 py-2"
                                                >
                                                    {filterStats.PENDING_SIGNATURE_CLIENT_FALSE}
                                                </Badge>
                                            </Nav.Link>
                                        </Nav.Item>

                                        {/* À Signer par HDS */}
                                        <Nav.Item>
                                            <Nav.Link
                                                className={`filter-nav-link d-flex justify-content-between align-items-center py-3 px-4 ${activeFilter === 'PENDING_SIGNATURE_CLIENT_TRUE' ? 'active-approved' : ''}`}
                                                onClick={() => handleFilterChange('PENDING_SIGNATURE_CLIENT_TRUE')}
                                                style={{
                                                    cursor: 'pointer',
                                                    borderRadius: 0,
                                                    borderLeft: activeFilter === 'PENDING_SIGNATURE_CLIENT_TRUE' ? '4px solid #28a745' : '4px solid transparent',
                                                    transition: 'all 0.3s ease',
                                                    backgroundColor: activeFilter === 'PENDING_SIGNATURE_CLIENT_TRUE' ? '#d4edda' : 'transparent'
                                                }}
                                            >
                                                <span className="d-flex align-items-center">
                                                    <FaSignature className="me-2" style={{ color: activeFilter === 'PENDING_SIGNATURE_CLIENT_TRUE' ? '#28a745' : '#6c757d' }} />
                                                    <span className={`fw-${activeFilter === 'PENDING_SIGNATURE_CLIENT_TRUE' ? 'bold' : 'medium'}`}>
                                                        À Signer par DHS
                                                    </span>
                                                </span>
                                                <Badge
                                                    bg={activeFilter === 'PENDING_SIGNATURE_CLIENT_TRUE' ? 'success' : 'light'}
                                                    text={activeFilter === 'PENDING_SIGNATURE_CLIENT_TRUE' ? 'white' : 'secondary'}
                                                    pill
                                                    className="px-3 py-2"
                                                >
                                                    {filterStats.PENDING_SIGNATURE_CLIENT_TRUE}
                                                </Badge>
                                            </Nav.Link>
                                        </Nav.Item>

                                        {/* Contrats Actifs */}
                                        <Nav.Item>
                                            <Nav.Link
                                                className={`filter-nav-link d-flex justify-content-between align-items-center py-3 px-4 ${activeFilter === 'ACTIVE' ? 'active-completed' : ''}`}
                                                onClick={() => handleFilterChange('ACTIVE')}
                                                style={{
                                                    cursor: 'pointer',
                                                    borderRadius: 0,
                                                    borderLeft: activeFilter === 'ACTIVE' ? '4px solid #007bff' : '4px solid transparent',
                                                    transition: 'all 0.3s ease',
                                                    backgroundColor: activeFilter === 'ACTIVE' ? '#cce7ff' : 'transparent'
                                                }}
                                            >
                                                <span className="d-flex align-items-center">
                                                    <FaCheckCircle className="me-2" style={{ color: activeFilter === 'ACTIVE' ? '#007bff' : '#6c757d' }} />
                                                    <span className={`fw-${activeFilter === 'ACTIVE' ? 'bold' : 'medium'}`}>Contrats Actifs</span>
                                                </span>
                                                <Badge
                                                    bg={activeFilter === 'ACTIVE' ? 'primary' : 'light'}
                                                    text={activeFilter === 'ACTIVE' ? 'white' : 'secondary'}
                                                    pill
                                                    className="px-3 py-2"
                                                >
                                                    {filterStats.ACTIVE}
                                                </Badge>
                                            </Nav.Link>
                                        </Nav.Item>

                                        {/* Contrats Finalisés */}
                                        <Nav.Item>
                                            <Nav.Link
                                                className={`filter-nav-link d-flex justify-content-between align-items-center py-3 px-4 ${activeFilter === 'COMPLETED' ? 'active-rejected' : ''}`}
                                                onClick={() => handleFilterChange('COMPLETED')}
                                                style={{
                                                    cursor: 'pointer',
                                                    borderRadius: 0,
                                                    borderLeft: activeFilter === 'COMPLETED' ? '4px solid #6c757d' : '4px solid transparent',
                                                    transition: 'all 0.3s ease',
                                                    backgroundColor: activeFilter === 'COMPLETED' ? '#e9ecef' : 'transparent'
                                                }}
                                            >
                                                <span className="d-flex align-items-center">
                                                    <FaTimesCircle className="me-2" style={{ color: activeFilter === 'COMPLETED' ? '#6c757d' : '#6c757d' }} />
                                                    <span className={`fw-${activeFilter === 'COMPLETED' ? 'bold' : 'medium'}`}>Contrats Finalisés</span>
                                                </span>
                                                <Badge
                                                    bg={activeFilter === 'COMPLETED' ? 'secondary' : 'light'}
                                                    text={activeFilter === 'COMPLETED' ? 'white' : 'secondary'}
                                                    pill
                                                    className="px-3 py-2"
                                                >
                                                    {filterStats.COMPLETED}
                                                </Badge>
                                            </Nav.Link>
                                        </Nav.Item>
                                    </Nav>
                                </Card.Body>
                            </Card>
                        </div>

                        {/* Contenu principal */}
                        <div className="col-lg-9">
                            {isLoading ? (
                                <div className="text-center py-5">
                                    <Spinner animation="border" variant="primary" size="lg" />
                                    <p className="mt-3 fs-5 text-muted">Chargement des contrats...</p>
                                </div>
                            ) : error ? (
                                <Alert variant="danger" className="mx-auto" style={{ maxWidth: '600px' }}>
                                    <div className="text-center">
                                        <FaExclamationTriangle size={32} className="mb-3 text-danger" />
                                        <h5 className="alert-heading">Erreur de chargement</h5>
                                        <p className="mb-3">{error}</p>
                                        <Button variant="primary" onClick={fetchContracts} size="lg">
                                            Réessayer le chargement
                                        </Button>
                                    </div>
                                </Alert>
                            ) : filteredContracts.length === 0 ? (
                                <div className="text-center py-5">
                                    <div className="py-5">
                                        <FaFileContract size={64} className="text-muted mb-4" />
                                        <h3 className="text-muted mb-3">
                                            {activeFilter === 'PENDING_SIGNATURE' ? 'Aucun contrat à générer' :
                                                activeFilter === 'PENDING_SIGNATURE_CLIENT_FALSE' ? 'Aucun contrat en attente du client' :
                                                    activeFilter === 'PENDING_SIGNATURE_CLIENT_TRUE' ? 'Aucun contrat à signer par HDS' :
                                                        activeFilter === 'ACTIVE' ? 'Aucun contrat actif' :
                                                            'Aucun contrat finalisé'}
                                        </h3>
                                        <p className="text-muted fs-5 mb-4">
                                            Aucun contrat ne correspond au filtre sélectionné.
                                        </p>
                                        <Button variant="primary" size="lg" onClick={fetchContracts}>
                                            Actualiser la liste
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                <Row xs={1} md={2} lg={3} className="g-4">
                                    {filteredContracts.map(contract => (
                                        <Col key={contract.id}>
                                            <Card className="h-100 shadow-lg border-0 hover-lift"
                                                  style={{
                                                      borderRadius: '16px',
                                                      transition: 'all 0.3s ease',
                                                      border: contract.contractStatus === 'ACTIVE' ? '2px solid #007bff' :
                                                          contract.contractStatus === 'PENDING_SIGNATURE' ? '2px solid #6c757d' :
                                                              contract.contractStatus === 'PENDING_SIGNATURE_CLIENT' ? '2px solid #ffc107' :
                                                                  '2px solid #6c757d'
                                                  }}>
                                                {/* Image du client */}
                                                <div className="position-relative">
                                                    <Card.Img
                                                        variant="top"
                                                        src={getAvatarUrl(contract.client?.user)}
                                                        style={{
                                                            height: '180px',
                                                            objectFit: 'cover',
                                                            borderTopLeftRadius: '16px',
                                                            borderTopRightRadius: '16px'
                                                        }}
                                                        onError={(e) => {
                                                            e.target.src = 'https://yrbhgwbzukfejvcphnwa.supabase.co/storage/v1/object/public/driven-home-services/media-service/dhs.png';
                                                        }}
                                                    />
                                                    <div className="position-absolute top-0 end-0 m-3">
                                                        {getStatusBadge(contract.contractStatus)}
                                                    </div>
                                                </div>

                                                <Card.Body className="d-flex flex-column p-4">
                                                    {/* Informations du contrat */}
                                                    <div className="mb-3">
                                                        <div className="d-flex align-items-center mb-2">
                                                            <div className="bg-primary rounded-circle p-2 me-2 d-flex align-items-center justify-content-center"
                                                                 style={{width: '40px', height: '40px'}}>
                                                                <FaFileContract size={16} className="text-white" />
                                                            </div>
                                                            <div>
                                                                <h6 className="mb-0 text-dark fw-bold">Contrat #{contract.contractRef}</h6>
                                                                <small className="text-muted">
                                                                    {getServiceLabel(contract.professional?.selectedService)}
                                                                </small>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Informations des parties */}
                                                    <div className="mb-3">
                                                        <div className="d-flex align-items-center mb-2">
                                                            <div className="bg-success rounded-circle p-2 me-2 d-flex align-items-center justify-content-center"
                                                                 style={{width: '32px', height: '32px'}}>
                                                                <FaUserTie size={12} className="text-white" />
                                                            </div>
                                                            <div>
                                                                <small className="text-dark fw-medium">Client</small>
                                                                <br />
                                                                <small className="text-muted">
                                                                    {getFullName(contract.client?.user)}
                                                                </small>
                                                            </div>
                                                        </div>

                                                        <div className="d-flex align-items-center mb-2">
                                                            <div className="bg-info rounded-circle p-2 me-2 d-flex align-items-center justify-content-center"
                                                                 style={{width: '32px', height: '32px'}}>
                                                                <FaUserTie size={12} className="text-white" />
                                                            </div>
                                                            <div>
                                                                <small className="text-dark fw-medium">Professionnel</small>
                                                                <br />
                                                                <small className="text-muted">
                                                                    {getFullName(contract.professional?.user)}
                                                                </small>
                                                            </div>
                                                        </div>
                                                    </div>


                                                    {/* Informations supplémentaires */}
                                                    <div className="mb-3">
                                                        {contract.serviceFee && (
                                                            <Badge bg="success" className="me-2 mb-2">
                                                                {new Intl.NumberFormat('fr-FR').format(contract.serviceFee)} FCFA
                                                            </Badge>
                                                        )}
                                                        {contract.contractDuration && (
                                                            <Badge bg="info" className="mb-2">
                                                                {contract.contractDuration} mois
                                                            </Badge>
                                                        )}
                                                    </div>

                                                    {/* Actions */}
                                                    <div className="mt-auto d-flex flex-column gap-2">
                                                        <Button
                                                            variant="primary"
                                                            className="w-100"
                                                            onClick={() => handleViewContract(contract)}
                                                            size="lg"
                                                            style={{background:'linear-gradient(135deg, #173247 0%, #3c6fcd 100%)'}}
                                                        >
                                                            <FaEye className="me-2" /> Voir détails
                                                        </Button>

                                                        {getActionButton(contract)}
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    ))}
                                </Row>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal pour afficher les détails du contrat */}
            <ContractModal
                contract={selectedContract}
                isOpen={isModalOpen}
                onClose={handleCloseModal}
            />




            {/* Modal pour afficher le PDF généré (juste après génération) */}
            {showPdfModal && (
                <div
                    className="contract-modal-overlay show"
                    style={{
                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 1060, overflowY: 'auto'
                    }}
                    onClick={closePdfModal}
                >
                    <div
                        className="contract-modal-container"
                        style={{
                            position: 'absolute', bottom: 0, left: 0, right: 0,
                            backgroundColor: 'white', borderRadius: '24px 24px 0 0',
                            maxHeight: '95vh', overflowY: 'auto', animation: 'slideUp 0.4s ease'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header fixe */}
                        <div className="modal-header-fixed" style={{
                            position: 'sticky', top: 0, left: 0, right: 0, zIndex: 100,
                            backgroundColor: 'white', borderBottom: '1px solid #e2e8f0',
                            borderTopLeftRadius: '24px', borderTopRightRadius: '24px', padding: '16px 24px'
                        }}>
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <h4 className="fw-bold mb-0 text-dark">
                                        <FaFilePdf className="me-2 text-danger" />
                                        Contrat Généré
                                    </h4>
                                    <p className="text-muted small mt-1 mb-0">
                                        Contrat #{selectedContract?.contractRef || 'N/A'}
                                    </p>
                                </div>
                                <button
                                    className="btn-close"
                                    onClick={closePdfModal}
                                ></button>
                            </div>
                        </div>

                        {/* Contenu principal */}
                        <div className="p-4">
                            {/* Message de succès */}
                            <div className="alert alert-success mb-4 d-flex align-items-center" style={{
                                borderRadius: '12px',
                                border: 'none',
                                backgroundColor: '#d4edda',
                                color: '#155724'
                            }}>
                                <FaCheckCircle className="me-2" size={20} />
                                <span>Contrat généré avec succès ! Le statut a été mis à jour vers "En attente de signature client".</span>
                            </div>

                            {/* Visualisation du PDF */}
                            <div className="bg-light rounded-3 p-3">
                                <div className="pdf-viewer-container" style={{
                                    minHeight: '500px',
                                    height: '60vh',
                                    backgroundColor: '#f8f9fa',
                                    borderRadius: '12px',
                                    overflow: 'hidden'
                                }}>
                                    {pdfData ? (
                                        <iframe
                                            src={pdfData}
                                            title={`Contrat-${selectedContract?.id}`}
                                            className="w-100 h-100 border-0"
                                            style={{ minHeight: '500px', height: '60vh' }}
                                        />
                                    ) : (
                                        <div className="text-center py-5 d-flex flex-column align-items-center justify-content-center" style={{ height: '100%' }}>
                                            <Spinner animation="border" variant="primary" size="lg" />
                                            <p className="mt-3 text-muted">Chargement du PDF...</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Footer fixe avec boutons d'action */}
                        <div className="modal-footer-fixed" style={{
                            position: 'sticky', bottom: 0, left: 0, right: 0,
                            backgroundColor: 'white', borderTop: '1px solid #e2e8f0',
                            padding: '16px 24px', borderBottomLeftRadius: '24px', borderBottomRightRadius: '24px'
                        }}>
                            <div className="d-flex justify-content-end gap-3">
                                <button
                                    onClick={closePdfModal}
                                    className="btn btn-outline-secondary rounded-pill px-4 d-flex align-items-center gap-2"
                                >
                                    <FaTimes /> Fermer
                                </button>
                                <button
                                    onClick={handleDownloadPdfFromModal}
                                    className="btn btn-danger rounded-pill px-4 d-flex align-items-center gap-2"
                                >
                                    <FaDownload /> Télécharger le PDF
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}










            {/* Modal générique pour afficher n'importe quel PDF présigné */}
            <PDFViewerModal
                show={showPdfViewer}
                onHide={closePdfViewer}
                pdfUrl={selectedPdfUrl}
                contractId={selectedPdfContractId}
                title={selectedPdfTitle}
                isLoading={pdfLoading}
            />

            {/* Modal pour la signature admin */}
            <AdminSignatureModal
                contract={contractToAdminSign}
                isOpen={showAdminSignatureModal}
                onClose={() => {
                    setShowAdminSignatureModal(false);
                    setContractToAdminSign(null);
                }}
                onSuccess={() => {
                    setShowAdminSignatureModal(false);
                    setContractToAdminSign(null);
                    fetchContracts();
                }}
            />
        </>
    );
};

export default ContractsPage;